import { c as _c } from "react/compiler-runtime";
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useReducer, useRef } from 'react';

/* eslint-disable react-hooks/refs -- Ref-based memoization: reads ref during render to avoid re-renders from setState. Cannot use setState-during-render because callers may pass unstable deps (new objects/functions each render), which would cause infinite re-render loops. */
const useDeepCompareMemoize = (value: React.DependencyList) => {
  const ref = useRef<React.DependencyList>(null);
  const signalRef = useRef<number>(0);
  if (!isEqual(value, ref.current)) {
    ref.current = value;
    signalRef.current += 1;
  }
  return [signalRef.current];
};
/* eslint-enable react-hooks/refs */

export function useSetState(initialState) {
  const $ = _c(2);
  const [state_0, setState] = useReducer(_temp, initialState);
  let t0;
  if ($[0] !== state_0) {
    t0 = [state_0, setState];
    $[0] = state_0;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp(state, newState) {
  return {
    ...state,
    ...newState
  };
}
export const useIsMountedRef = () => {
  const $ = _c(2);
  const isMounted = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isMounted;
};
export function useSafeSetState(initialState) {
  const $ = _c(6);
  const [state, setState] = useSetState(initialState);
  const isMounted = useIsMountedRef();
  let t0;
  if ($[0] !== isMounted || $[1] !== setState) {
    t0 = (...t1) => {
      const args = t1;
      return isMounted.current && setState(...args);
    };
    $[0] = isMounted;
    $[1] = setState;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const safeSetState = t0;
  let t1;
  if ($[3] !== safeSetState || $[4] !== state) {
    t1 = [state, safeSetState];
    $[3] = safeSetState;
    $[4] = state;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/* eslint-disable react-hooks/refs -- Ref-based previous value tracking: reads ref during render. Cannot use setState-during-render because callers may pass unstable object references, causing infinite re-render loops. */
export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
/* eslint-enable react-hooks/refs */

export function useDeepCompareEffect(effect: React.EffectCallback, dependencies: React.DependencyList) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are managed by useDeepCompareMemoize which triggers signal changes on deep inequality
  return useEffect(effect, useDeepCompareMemoize(dependencies));
}
export function useDeepCompareCallback<T extends (...args: any[]) => any>(callback: T, dependencies: React.DependencyList) {
  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo -- deps are managed by useDeepCompareMemoize which triggers signal changes on deep inequality
  return useCallback(callback, useDeepCompareMemoize(dependencies));
}
export const useUpdateEffect = (effect: React.EffectCallback, deps: React.DependencyList) => {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return effect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally dynamic deps forwarded from caller; effect is excluded to avoid re-triggering on every render
  }, deps);
};
export const useDebounce = (t0, t1, t2) => {
  const $ = _c(5);
  const fn = t0 === undefined ? _temp2 : t0;
  const ms = t1 === undefined ? 0 : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const deps = t3;
  let t4;
  if ($[2] !== fn || $[3] !== ms) {
    t4 = () => {
      const timeout = setTimeout(fn, ms);
      return () => {
        clearTimeout(timeout);
      };
    };
    $[2] = fn;
    $[3] = ms;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useUpdateEffect(t4, deps);
};
function _temp2() {}